<script setup lang="ts">
import type { ComponentPublicInstance } from "vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { SelectOption, SelectProps } from "@/dsComponents/inputs/types";

import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";

const props = withDefaults(defineProps<SelectProps>(), {
    size: "md",
    width: "300px",
    icon: "chevron-down",
    variant: "stroke",
});

const { t } = useI18n();

const emit = defineEmits<{
    "update:modelValue": [value: string];
    select: [option: SelectOption];
}>();

const focus = ref(false);

const handleSelect = (option: SelectOption) => {
    if (props.disabled) return;
    focus.value = false;
    emit("select", option);
    emit("update:modelValue", option.value);
};

const handleDropdownClickAway = (name: supporttedEventNames) => {
    if (name === "clickaway") focus.value = false;
};

const search = ref("");
const displayedValue = computed(() => {
    const optionFound = props.options.find((opt) => opt.value === props.modelValue);
    return optionFound?.label ?? props.modelValue;
});

const filterOption = (value: string) => {
    search.value = value;
};

const filteredOptions = computed<SelectOption[]>(() => {
    if (!search.value) {
        return props.options;
    }
    const searchRegex = new RegExp(search.value, "i");
    return props.options.filter((option) => searchRegex.test(String(option.label)));
});

// Active option index with quick search and navigation.
const activeIndex = ref(-1);
const dropdownItems = ref<(ComponentPublicInstance | null)[]>();

// Update CSS class and scroll on change.
watch(activeIndex, () => {
    const activeClass = "active";
    if (!dropdownItems.value) return;
    dropdownItems.value.forEach((item) => {
        if (!item?.$el) return;
        const htmlEl = item.$el as HTMLElement;
        htmlEl.classList.remove(activeClass);
    });
    if (activeIndex.value > -1) {
        const instance = dropdownItems.value[activeIndex.value];
        const dropdownItem = instance?.$el as HTMLElement | undefined;
        if (dropdownItem) {
            dropdownItem.classList.add(activeClass);
            const dimension = dropdownItem.getBoundingClientRect();
            scrollTo.value = dimension.top - 8;
        }
    }
});

const scrollTo = ref<number>(0);
const lastDate = ref<number>(0);
const quickSearch = ref<string>("");

const considerDiacritics = (str: string) => {
    return str
        .replaceAll("e", "(e|é|è|ê|ë)")
        .replaceAll("a", "(a|à|â)")
        .replaceAll("i", "(i|î|ï)")
        .replaceAll("u", "(u|û|ù)")
        .replaceAll("o", "(o|ô)");
};

const handleQuickSearch = (event: KeyboardEvent) => {
    const date = Date.now();
    if (event.key.match(/^[a-z]$/gi) && event.key.length === 1) {
        if (lastDate.value === 0 || date - lastDate.value <= 500) {
            quickSearch.value += event.key;
        } else {
            quickSearch.value = event.key;
        }
        lastDate.value = date;
        // biome-ignore lint/style/useTemplate: Init Biome
        const searchRegex = new RegExp("^" + considerDiacritics(quickSearch.value), "i");
        const optionIndex = props.options.findIndex((option) => searchRegex.test(String(option.label)));
        activeIndex.value = optionIndex;
    } else if (["ArrowUp", "ArrowDown"].includes(event.code)) {
        if (event.code === "ArrowUp" && activeIndex.value > -1) activeIndex.value--;
        else if (event.code === "ArrowDown" && activeIndex.value < props.options.length) activeIndex.value++;
    } else if (event.code === "Enter" && activeIndex.value > -1) {
        handleSelect(props.options[activeIndex.value]);
        activeIndex.value = -1;
    }
};

const handleFocus = () => {
    if (props.disabled) return;
    focus.value = !focus.value;
};

const preventScrolling = (event: KeyboardEvent) => {
    if (["ArrowUp", "ArrowDown"].includes(event.code)) event.preventDefault();
};

watch(focus, () => {
    // Mimics native select quick search and navigation with arrows (up / down).
    if (focus.value) {
        window.addEventListener("keyup", handleQuickSearch);
        window.addEventListener("keydown", preventScrolling);
    } else {
        window.removeEventListener("keyup", handleQuickSearch);
        window.removeEventListener("keydown", preventScrolling);
    }
});

const optionContainer = ref();
watch(scrollTo, () => {
    const scrollableContent = optionContainer.value;
    if (scrollTo.value && scrollableContent) {
        const dimension = scrollableContent.getBoundingClientRect();
        const computedTop = scrollTo.value + scrollableContent.scrollTop - dimension.top;
        scrollableContent?.scrollTo({ top: computedTop, behavior: "smooth" });
    }
});
</script>
<template>
    <div class="ds-select">
        <ControlledDropdown
            :width="width"
            :open="focus"
            :fit-content="fitContent"
            :max-height="maxHeight"
            :direction="direction"
            @change="handleDropdownClickAway"
        >
            <template #trigger-zone>
                <div class="select-wrapper">
                    <label v-if="label" :for="label" class="typo-xm-medium">
                        {{ label }}
                        <span v-if="required" class="required-asterisk">*</span>
                    </label>
                    <div
                        :id="label"
                        :class="['select-holder', size, variant, { focus, error, disabled, placeholder: !displayedValue }]"
                        :style="{ width }"
                        @click="handleFocus"
                    >
                        <div class="select-holder-content">
                            <i v-if="triggerIconLeft" :class="['icon-left', 'ti', `ti-${triggerIconLeft}`]" />
                            <span>{{ displayedValue || props.placeholder }}</span>
                        </div>
                        <div class="icon-container">
                            <i :class="`ti ti-${icon}`"></i>
                        </div>
                    </div>
                </div>
            </template>
            <template #dropdown-content>
                <div class="dropdown-content-wrapper">
                    <slot name="option-header" :filterOption="filterOption"></slot>
                    <div ref="optionContainer" class="option-container" :style="{ maxHeight: '320px' }">
                        <slot>
                            <!-- biome-ignore lint/correctness/useVueVForKey: Init Biome -->
                            <DropdownItem v-for="option of filteredOptions" v-bind="option" @click="() => handleSelect(option)" ref="dropdownItems" />
                            <div class="no-result" v-if="filteredOptions.length === 0">{{ t("global.noResults") }}</div>
                        </slot>
                    </div>
                    <div class="option-footer" v-if="$slots['option-footer']">
                        <slot name="option-footer" :resultCount="filteredOptions?.length ?? 0"></slot>
                    </div>
                </div>
            </template>
        </ControlledDropdown>
    </div>
</template>
<style scoped lang="scss">
@mixin inputShadow($color) {
    box-shadow: 0 0 0 3px $color;
}

$transition-time: 0.2s;
.ds-select {
    .select-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-sm, 6px);

        .required-asterisk {
            color: var(--blue-base, #2051f8);
        }

        .select-holder {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: var(--spacing-sm, 6px);
            height: 36px;
            overflow: hidden;
            width: 100%;
            padding: 0 var(--spacing-lg, 12px);
            border-radius: var(--radius-md, 8px);
            transition: $transition-time;
            white-space: nowrap;
            @include font-size-13;
            font-weight: 400;

            &-content {
                display: flex;
                align-items: center;
                gap: var(--spacing-md, 8px);
            }

            .icon-container {
                display: flex;
                gap: var(--spacing-sm, 6px);
            }

            &.stroke {
                border: 1px solid var(--stroke-soft-200, #dbdfe6ff);
            }

            &.ghost,
            &.transparent,
            &.grey,
            &.grey2 {
                .icon-container {
                    display: none;
                }
            }

            &.placeholder {
                color: var(--text-soft-400, #9297aa);
            }

            &:not(.placeholder) {
                color: var(--text-main-900, #00061a);
            }

            span {
                text-overflow: ellipsis;
                overflow: hidden;
                &.value {
                    color: var(--text-main-900, #00061a);
                }
            }

            .ti {
                font-size: 16px;
            }

            &.xs {
                height: 28px;
                .ti {
                    font-size: 14px;
                }
            }

            &.sm {
                height: 36px;
            }

            &.md {
                height: 40px;
            }

            &.lg {
                height: 44px;
                input {
                    @include font-size-14;
                }
            }

            &:hover:not(.disabled, .focus, .error) {
                &.ghost {
                    border-color: var(--stroke-soft-200, #dbdfe6ff);
                }
                &.stroke {
                    border-color: var(--stroke-strong-900, #00061aff);
                }
                &.grey {
                    background-color: var(--neutral-50, #f6f7f9ff);
                }
                &.grey2 {
                    background-color: var(--neutral-100, #eff2f5ff);
                }
            }

            &.focus {
                border-color: $blue-base;
            }

            &.error {
                border-color: $red-base;
            }

            &:hover {
                &.ghost,
                &.transparent,
                &.grey,
                &.grey2 {
                    .icon-container {
                        display: flex;
                        gap: var(--spacing-sm, 6px);
                    }
                }
            }
        }
    }
}

.option-footer {
    padding: var(--spacing-md, 8px) var(--spacing-xl, 16px);
}

.dropdown-content-wrapper {
    .option-container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs, 4px);
        overflow: auto;
    }
}
</style>
<style lang="scss">
.no-result {
    padding: var(--spacing-md, 8px);
    color: var(--text-main-900, #00061a);
    text-align: center;
    font-weight: 500;
}
</style>
